<template>
  <div v-if="isUserAccount && userStores.length > 0" class="store-switcher-wrap">
    <v-select
      :model-value="selectedStoreId"
      :items="storeOptions"
      item-title="text"
      item-value="value"
      density="compact"
      variant="outlined"
      hide-details
      menu-icon="expand_more"
      class="store-switcher"
      @update:model-value="handleStoreChange"
    />
    <v-dialog v-model="showAllStoresModal" max-width="480">
      <v-card>
        <v-card-title class="text-h6">All Stores mode</v-card-title>
        <v-card-text class="text-sm">
          All Stores works on Dashboard and Profit & Loss. Choose where you want to continue.
        </v-card-text>
        <v-card-actions class="flex flex-col gap-2 sm:flex-row sm:justify-end pa-4 pt-0">
          <v-btn variant="outlined" @click="cancelAllStoresSwitch">Cancel</v-btn>
          <v-btn variant="outlined" @click="goToAllStoresProfitLoss">Go to Profit & Loss</v-btn>
          <v-btn color="primary" @click="goToAllStoresDashboard">Go to Dashboard</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'

import { useAuthStore } from '../../stores/auth-store'
import { authService } from '../../services/auth'
import { ALL_STORES_VALUE } from '../../stores/auth-store'

const router = useRouter()

const authStore = useAuthStore()
const { userStores } = storeToRefs(authStore)

const isUserAccount = computed(() => authService.isUserToken())
const selectedStoreId = ref<string | null>(null)
const showAllStoresModal = ref(false)
const previousStoreId = ref<string | null>(null)

const storeOptions = computed(() => {
  const storeList = userStores.value.map((store) => ({
    value: String(store.id),
    text: store.name || store.domain || `Store ${store.id}`,
  }))
  return [{ value: ALL_STORES_VALUE, text: 'All Stores' }, ...storeList]
})

const syncSelectedStoreFromLocalStorage = () => {
  if (isUserAccount.value && userStores.value.length > 0) {
    const storedStoreId = localStorage.getItem('selected_store')
    if (storedStoreId === ALL_STORES_VALUE) {
      if (selectedStoreId.value !== ALL_STORES_VALUE) selectedStoreId.value = ALL_STORES_VALUE
      return
    }
    if (storedStoreId) {
      const matchingStore = userStores.value.find((s) => String(s.id) === String(storedStoreId))
      if (matchingStore) {
        const storeIdStr = String(matchingStore.id)
        if (selectedStoreId.value !== storeIdStr) selectedStoreId.value = storeIdStr
        return
      }
    }

    const firstStoreId = String(userStores.value[0].id)
    if (selectedStoreId.value !== firstStoreId) selectedStoreId.value = firstStoreId
    return
  }

  selectedStoreId.value = null
}

onMounted(() => {
  syncSelectedStoreFromLocalStorage()
  window.addEventListener('storage', syncSelectedStoreFromLocalStorage)
})

onBeforeUnmount(() => {
  window.removeEventListener('storage', syncSelectedStoreFromLocalStorage)
})

watch([() => userStores.value, () => isUserAccount.value], () => syncSelectedStoreFromLocalStorage(), {
  immediate: true,
  deep: true,
})

watch(
  () => router.currentRoute.value.name,
  () => {
    syncSelectedStoreFromLocalStorage()
  },
)

/** Full page load so all views pick up the new shop context (headers, Pinia, cached data). */
const reloadToRoute = (name: 'dashboard' | 'dashboard-all-stores' | 'profit-loss' | 'profit-loss-all-stores') => {
  window.location.href = router.resolve({ name }).href
}

/** Same localStorage keys as `updateSelectedStore`, without waiting on network — reload runs immediately after. */
const persistSelectedStoreToStorage = (storeId: string): boolean => {
  const stores = authStore.userStores
  if (storeId === ALL_STORES_VALUE) {
    if (!stores.length) return false
    localStorage.setItem('selected_store', ALL_STORES_VALUE)
    localStorage.setItem('shop_id', String(stores[0].id))
  } else {
    localStorage.setItem('selected_store', storeId)
    localStorage.setItem('shop_id', storeId)
  }
  if (storeId !== ALL_STORES_VALUE) {
    localStorage.setItem('last_single_store', storeId)
  }
  authStore.syncSelectedStoreLocalIdFromStorage()
  return true
}

const handleStoreChange = (storeId: string) => {
  const oldStoreId = selectedStoreId.value
  if (!storeId || oldStoreId === storeId) return

  previousStoreId.value = oldStoreId
  selectedStoreId.value = storeId

  const currentRouteName = String(router.currentRoute.value.name || '')
  const supportedAllStoresRoutes = new Set([
    'dashboard',
    'dashboard-all-stores',
    'profit-loss',
    'profit-loss-all-stores',
  ])
  if (storeId === ALL_STORES_VALUE && !supportedAllStoresRoutes.has(currentRouteName)) {
    showAllStoresModal.value = true
    return
  }

  if (!persistSelectedStoreToStorage(storeId)) {
    selectedStoreId.value = oldStoreId
    return
  }

  const currentRoute = router.currentRoute.value
  const onDashboard = currentRoute.name === 'dashboard' || currentRoute.name === 'dashboard-all-stores'
  const onProfitLoss = currentRoute.name === 'profit-loss' || currentRoute.name === 'profit-loss-all-stores'
  if (onDashboard) {
    reloadToRoute(storeId === ALL_STORES_VALUE ? 'dashboard-all-stores' : 'dashboard')
    return
  }
  if (onProfitLoss) {
    reloadToRoute(storeId === ALL_STORES_VALUE ? 'profit-loss-all-stores' : 'profit-loss')
    return
  }
  window.location.reload()
}

const cancelAllStoresSwitch = () => {
  showAllStoresModal.value = false
  selectedStoreId.value = previousStoreId.value
  previousStoreId.value = null
}

const commitAllStoresSwitchAndNavigate = (routeName: 'dashboard-all-stores' | 'profit-loss-all-stores') => {
  if (!persistSelectedStoreToStorage(ALL_STORES_VALUE)) {
    cancelAllStoresSwitch()
    return
  }
  showAllStoresModal.value = false
  previousStoreId.value = null
  reloadToRoute(routeName)
}

const goToAllStoresDashboard = () => {
  commitAllStoresSwitchAndNavigate('dashboard-all-stores')
}

const goToAllStoresProfitLoss = () => {
  commitAllStoresSwitchAndNavigate('profit-loss-all-stores')
}
</script>

<style scoped>
.store-switcher-wrap {
  min-width: 0;
  width: 100%;
}

.store-switcher {
  min-width: 0;
  width: 100%;
}

.store-switcher :deep(.v-field__input) {
  font-size: 0.875rem;
  font-weight: 500;
}

.store-switcher :deep(.v-select__selection-text) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
